import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { DICTIONARY } from '@/constants/testIds';
import { Plus, MagnifyingGlass, DotsThreeVertical, PencilSimple, Archive, ArrowCounterClockwise, ShieldStar } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const CATEGORIES = ['symptom', 'vital', 'mood', 'function', 'medication', 'lab', 'other'];
const DATA_TYPES = ['yes-no', 'number', 'scale', 'choice', 'text'];
const CODE_SYSTEMS = ['none', 'LOINC', 'SNOMED CT', 'RxNorm'];

function ConceptForm({ initial, onSaved }) {
    const isEdit = !!initial;
    const [f, setF] = useState({
        key: initial?.key || '',
        display_name: initial?.display_name || '',
        category: initial?.category || 'symptom',
        data_type: initial?.data_type || 'yes-no',
        unit: initial?.unit || '',
        code_system: initial?.code_system || 'none',
        code: initial?.code || '',
    });
    const save = async () => {
        if (!f.key.trim() || !f.display_name.trim()) { toast.error('Key and display name are required'); return; }
        const payload = {
            display_name: f.display_name.trim(),
            category: f.category,
            data_type: f.data_type,
            unit: f.unit || null,
            code_system: f.code_system,
            code: f.code || null,
        };
        try {
            if (isEdit) {
                await api.patch(`/admin/concepts/${initial.key}`, payload);
            } else {
                await api.post('/admin/concepts', { key: f.key.trim(), ...payload });
            }
            toast.success('Saved'); onSaved?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Save failed');
        }
    };
    return (
        <>
            <div className="space-y-3">
                <div>
                    <Label>Concept key</Label>
                    <Input
                        data-testid={DICTIONARY.keyInput}
                        value={f.key}
                        onChange={e => setF({ ...f, key: e.target.value })}
                        placeholder="snake_case_key"
                        disabled={isEdit}
                    />
                    <div className="text-[11px] text-slate-500 mt-1">Unique identifier — locked once created.</div>
                </div>
                <div>
                    <Label>Display name</Label>
                    <Input data-testid={DICTIONARY.displayInput} value={f.display_name} onChange={e => setF({ ...f, display_name: e.target.value })} />
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <Label>Category</Label>
                        <Select value={f.category} onValueChange={v => setF({ ...f, category: v })}>
                            <SelectTrigger data-testid={DICTIONARY.categorySelect}><SelectValue /></SelectTrigger>
                            <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Data type</Label>
                        <Select value={f.data_type} onValueChange={v => setF({ ...f, data_type: v })}>
                            <SelectTrigger data-testid={DICTIONARY.dataTypeSelect}><SelectValue /></SelectTrigger>
                            <SelectContent>{DATA_TYPES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <div>
                        <Label>Unit</Label>
                        <Input data-testid={DICTIONARY.unitInput} value={f.unit} onChange={e => setF({ ...f, unit: e.target.value })} placeholder="mg/dL, lbs…" />
                    </div>
                    <div>
                        <Label>Code system</Label>
                        <Select value={f.code_system} onValueChange={v => setF({ ...f, code_system: v })}>
                            <SelectTrigger data-testid={DICTIONARY.codeSystemSelect}><SelectValue /></SelectTrigger>
                            <SelectContent>{CODE_SYSTEMS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Code</Label>
                        <Input data-testid={DICTIONARY.codeInput} value={f.code} onChange={e => setF({ ...f, code: e.target.value })} placeholder="72514-3" />
                    </div>
                </div>
            </div>
            <DialogFooter>
                <Button data-testid={DICTIONARY.submit} onClick={save} className="rounded-full bg-primary text-primary-foreground">
                    {isEdit ? 'Save changes' : 'Add concept'}
                </Button>
            </DialogFooter>
        </>
    );
}

export default function DictionarySettings() {
    const qc = useQueryClient();
    const { canMutateAdmin } = useAuth();
    const [q, setQ] = useState('');
    const [addOpen, setAddOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [showArchived, setShowArchived] = useState(true);

    const { data: concepts = [], isLoading } = useQuery({
        queryKey: ['admin-concepts'],
        queryFn: async () => (await api.get('/admin/concepts', { params: { include_archived: true } })).data,
    });

    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        return concepts.filter(c => {
            if (!showArchived && c.status === 'Archived') return false;
            if (!s) return true;
            return c.key.toLowerCase().includes(s) || c.display_name.toLowerCase().includes(s);
        });
    }, [concepts, q, showArchived]);

    const invalidate = () => qc.invalidateQueries({ queryKey: ['admin-concepts'] });

    const archive = async (c) => {
        try { await api.post(`/admin/concepts/${c.key}/archive`); toast.success(`Archived ${c.key}`); invalidate(); }
        catch { toast.error('Failed'); }
    };
    const restore = async (c) => {
        try { await api.post(`/admin/concepts/${c.key}/restore`); toast.success(`Restored ${c.key}`); invalidate(); }
        catch { toast.error('Failed'); }
    };

    return (
        <div data-testid={DICTIONARY.root} className="px-6 md:px-10 py-8 max-w-5xl">
            <header className="mb-6 flex items-end justify-between gap-4">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
                        <ShieldStar size={12} weight="duotone" className="text-primary" /> Admin · Settings
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 mt-1">Data Dictionary</h1>
                    <p className="text-slate-600 mt-2 max-w-2xl text-sm">
                        A managed catalog of concepts used to tag question-level answers for structured export. Only admins see this — coordinators and family members never see codes, tags, or the dictionary.
                    </p>
                </div>
                {canMutateAdmin && (
                    <Dialog open={addOpen} onOpenChange={setAddOpen}>
                        <DialogTrigger asChild>
                            <Button data-testid={DICTIONARY.addButton} className="rounded-full bg-primary text-primary-foreground">
                                <Plus size={16} className="mr-1" /> Add concept
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader><DialogTitle className="font-heading text-2xl">New concept</DialogTitle></DialogHeader>
                            <ConceptForm onSaved={() => { setAddOpen(false); invalidate(); }} />
                        </DialogContent>
                    </Dialog>
                )}
            </header>

            <div className="flex items-center gap-3 mb-4">
                <div className="relative flex-1 max-w-md">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input data-testid={DICTIONARY.search} value={q} onChange={e => setQ(e.target.value)} placeholder="Search key or name" className="pl-9" />
                </div>
                <label className="flex items-center gap-2 text-xs text-slate-600">
                    <input type="checkbox" data-testid={DICTIONARY.showArchivedToggle} checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
                    Show archived
                </label>
            </div>

            {isLoading ? (
                <div className="text-sm text-slate-500 py-10 text-center">Loading…</div>
            ) : filtered.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center text-sm text-slate-500">
                    No concepts match your search.
                </div>
            ) : (
                <div className="rounded-xl border border-slate-200 bg-card shadow-sm overflow-hidden">
                    <div className="grid grid-cols-[1fr_1.4fr_100px_100px_120px_140px_40px] gap-3 px-5 py-2 text-[10px] uppercase tracking-widest text-slate-500 bg-slate-50/60 border-b border-slate-200">
                        <div>Key</div><div>Display name</div><div>Category</div><div>Data type</div><div>Code</div><div>Status</div><div></div>
                    </div>
                    {filtered.map(c => (
                        <div key={c.key} data-testid={DICTIONARY.row}
                             className="grid grid-cols-[1fr_1.4fr_100px_100px_120px_140px_40px] gap-3 px-5 py-3 items-center border-t border-slate-100 first:border-t-0">
                            <div className="font-mono text-xs text-slate-800 truncate">{c.key}</div>
                            <div className="text-sm text-slate-800">
                                {c.display_name}
                                {c.unit && <span className="text-slate-500"> · {c.unit}</span>}
                            </div>
                            <div className="text-xs text-slate-600">{c.category}</div>
                            <div className="text-xs text-slate-600">{c.data_type}</div>
                            <div className="text-xs text-slate-600">
                                {c.code_system && c.code_system !== 'none'
                                    ? <span className="font-mono">{c.code_system} {c.code || '—'}</span>
                                    : <span className="text-slate-400">—</span>}
                            </div>
                            <div>
                                {c.status === 'Active'
                                    ? <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded bg-emerald-50 text-emerald-800">Active</span>
                                    : <span className="text-[10px] uppercase tracking-widest font-semibold px-2 py-0.5 rounded bg-slate-100 text-slate-600">Archived</span>}
                            </div>
                            {canMutateAdmin ? (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="p-1.5 rounded hover:bg-slate-100 text-slate-500 justify-self-end" data-testid={`${DICTIONARY.row}-menu-${c.key}`}>
                                            <DotsThreeVertical size={18} weight="bold" />
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem data-testid={`${DICTIONARY.edit}-${c.key}`} onClick={() => setEdit(c)}>
                                            <PencilSimple size={14} className="mr-2" /> Edit
                                        </DropdownMenuItem>
                                        {c.status === 'Active' ? (
                                            <DropdownMenuItem data-testid={`${DICTIONARY.archive}-${c.key}`} onClick={() => archive(c)} className="text-amber-800">
                                                <Archive size={14} className="mr-2" /> Archive
                                            </DropdownMenuItem>
                                        ) : (
                                            <DropdownMenuItem data-testid={`${DICTIONARY.restore}-${c.key}`} onClick={() => restore(c)} className="text-emerald-800">
                                                <ArrowCounterClockwise size={14} className="mr-2" /> Restore
                                            </DropdownMenuItem>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            ) : (
                                <span />
                            )}
                        </div>
                    ))}
                </div>
            )}

            <Dialog open={!!edit} onOpenChange={v => !v && setEdit(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle className="font-heading text-2xl">Edit concept</DialogTitle></DialogHeader>
                    {edit && <ConceptForm initial={edit} onSaved={() => { setEdit(null); invalidate(); }} />}
                </DialogContent>
            </Dialog>
        </div>
    );
}
